'use client';

/**
 * useTimeoutRecovery — everything the handoff-timeout recovery UI needs.
 *
 * A handoff can time out with no agent available. When that happens the user
 * is offered a "leave your details" form (HandoffTimeoutPanel), and the
 * presence ribbon has to know when to stop showing its TIMED_OUT variant.
 * Both surfaces — the full page and the embedded panel — need the same
 * answers.
 *
 * Extracted rather than copied, deliberately. page.tsx's own comment on the
 * panel mount warns that "a second mount path for the same stateful form
 * would be a divergence bug waiting to happen", and that applies just as much
 * to the derivations feeding it: `showTimeoutPanel` and
 * `timeoutPanelHandoffId` encode non-obvious precedence rules (a live handoff
 * always beats stale restored data; its mere presence suppresses the restored
 * fallback regardless of phase) that would drift the moment they existed
 * twice. One implementation, two consumers.
 *
 * Call inside a ChatProvider.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useChatContext } from '@/contexts/ChatContext';
import { useConversationStore } from '@/stores/conversation-store';
import { outstandingTimeoutOffer } from '@/lib/handoff-timeout-restore';
import { hasResumedChatAfterTimeout } from '@/lib/presence-ribbon-text';
import { HandoffPhase, HandoffEndReason } from '@/types/chat';
import type { ContactSummary } from '@/types/chat';

export interface TimeoutRecovery {
  /** Should HandoffTimeoutPanel be mounted at all? */
  showTimeoutPanel: boolean;
  /** The single handoffId the recovery UI is FOR (live wins over restored). */
  timeoutPanelHandoffId: string | undefined;
  /** Contact details already on file, so the panel can prefill. */
  contactSummaryForHandoff: ContactSummary | undefined;
  /** Durable conversation identity the panel posts against. */
  conversationId: string | null;
  /** True once the user has resumed ordinary chatting after a timeout —
   *  PresenceRibbon uses it to retire the TIMED_OUT variant (Defect 7). */
  timeoutRecoveryWindowClosed: boolean;
}

export function useTimeoutRecovery(): TimeoutRecovery {
  const { handoff, messages } = useChatContext();
  const conversationId = useConversationStore((s) => s.conversationId);

  // ── Recovery window: closes once the user resumes ordinary chatting ──
  //
  // See presence-ribbon-text.ts's hasResumedChatAfterTimeout for the "why
  // USER-authored, why a baseline" reasoning — this effect just supplies
  // that baseline: the transcript length as of the last render where this
  // handoff was NOT yet TIMED_OUT. One-shot per handoffId once resumed — a
  // handoff never leaves TIMED_OUT within a session, so nothing to re-arm.
  const preTimeoutMessagesLenRef = useRef<Record<string, number>>({});
  const [resumedChatAfterTimeout, setResumedChatAfterTimeout] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    if (!handoff) return;
    if (handoff.phase !== HandoffPhase.TIMED_OUT) {
      preTimeoutMessagesLenRef.current[handoff.handoffId] = messages.length;
      return;
    }
    if (resumedChatAfterTimeout[handoff.handoffId]) return;
    const sinceIndex =
      preTimeoutMessagesLenRef.current[handoff.handoffId] ?? messages.length;
    if (hasResumedChatAfterTimeout(messages, sinceIndex)) {
      setResumedChatAfterTimeout((prev) => ({
        ...prev,
        [handoff.handoffId]: true,
      }));
    }
  }, [handoff, messages, resumedChatAfterTimeout]);

  // A restored `handoff_timeout_offer` marker (agent/manager.py's
  // persist_handoff_timeout_offer) whose offer hasn't since been fulfilled by
  // a support-request receipt — see lib/handoff-timeout-restore.ts for the
  // fulfillment rule. Undefined on a fresh/live-only session, or once acted on.
  const restoredTimeoutOffer = useMemo(
    () => outstandingTimeoutOffer(messages),
    [messages]
  );

  // The panel must survive past the dedicated TIMED_OUT phase into a
  // subsequent HANDOFF_ENDED(timeout_no_agent) — see handoff-recap-card.tsx's
  // endReason guard, which suppresses the generic recap for exactly this case
  // so the panel isn't fighting a duplicate "chat ended" treatment.
  const isHandoffTimedOut =
    handoff?.phase === HandoffPhase.TIMED_OUT ||
    (handoff?.phase === HandoffPhase.ENDED &&
      handoff?.endReason === HandoffEndReason.TIMEOUT_NO_AGENT);

  // Single resolved handoffId the recovery UI is FOR — a live `handoff`
  // always wins (a fresh WS object must never lose to stale restored data),
  // falling back to a restored outstanding offer only when there's no live
  // handoff at all (the reload case). Both the contact-summary lookup and the
  // panel mount read off this ONE value so they can never resolve to two
  // different handoffs.
  const timeoutPanelHandoffId =
    handoff?.handoffId ?? restoredTimeoutOffer?.handoffId;

  // Distinct from `timeoutPanelHandoffId`, which resolves to a value even
  // when a LIVE handoff exists but isn't a timeout (WAITING/CONNECTED): in
  // that case a stale restored offer must not resurface just because
  // `handoff` briefly isn't set on some render, so a live handoff's mere
  // presence (regardless of phase) suppresses the restored fallback.
  const showTimeoutPanel =
    (isHandoffTimedOut && !!handoff) || (!handoff && !!restoredTimeoutOffer);

  // Resolve the CONTACT_SUMMARY row tied to the ACTIVE handoff (live or
  // restored) so the panel can prefill "We'll reply to {email}" / skip asking
  // for a phone already on file, without ChatContext needing a dedicated
  // field: insertContactSummary keys that row's id as
  // `contact-summary-${handoff_id}`, and that row IS persisted/restored, so
  // email/phone recover across a reload too.
  const contactSummaryForHandoff = useMemo(() => {
    if (!timeoutPanelHandoffId) return undefined;
    const row = messages.find(
      (m) => m.id === `contact-summary-${timeoutPanelHandoffId}`
    );
    return row?.contactSummary;
  }, [timeoutPanelHandoffId, messages]);

  return {
    showTimeoutPanel,
    timeoutPanelHandoffId,
    contactSummaryForHandoff,
    conversationId,
    timeoutRecoveryWindowClosed:
      !!handoff && !!resumedChatAfterTimeout[handoff.handoffId],
  };
}
